import {
    IonButton, IonButtons, IonCard,
    IonCardHeader, IonCardSubtitle, IonCardTitle,
    IonContent, IonHeader, IonIcon, IonInput,
    IonItem,
    IonItemDivider,
    IonLabel, IonList, IonModal, IonNote,
    IonRefresher, IonRefresherContent,
    IonSearchbar, IonSpinner, IonTitle, IonToolbar,
    useIonAlert, useIonLoading, useIonToast
} from "@ionic/react";
import React, {useCallback, useEffect, useState} from "react";
import {add, pencil} from "ionicons/icons";
import {errorToastOptions} from "../../components/ErrorToast";
import {
    createService,
    getServiceType,
    setServiceTypeDisabled,
    setServiceTypeName,
    updateService
} from "../../repositories/adminServiceTypeRepository";

export const AdminViewServiceType = ({serviceTypeId}) => {
    const [serviceType, setServiceType] = useState(null);
    const [services, setServices] = useState([]);
    const [search, setSearch] = useState('');
    // service being created ({}) or edited ({serviceId, ...}), null when the modal is closed
    const [editedService, setEditedService] = useState(null);
    const [presentAlert] = useIonAlert();
    const [presentLoading, dismissLoading] = useIonLoading();
    const [presentToast] = useIonToast();

    const presentError = useCallback(async (e, fallbackMessage) => {
        const message = typeof e.response?.data === 'string' && e.response.data ? e.response.data : fallbackMessage;
        await presentToast(errorToastOptions(message));
    }, [presentToast]);

    const loadServiceType = useCallback(async () => {
        try {
            const response = await getServiceType(serviceTypeId);
            setServiceType(response.serviceType);
            setServices(response.services);
        } catch (e) {
            await presentError(e, "Failed to load service type");
        }
    }, [serviceTypeId, presentError]);

    useEffect(() => {
        setServiceType(null);
        loadServiceType();
    }, [loadServiceType]);

    const reload = async (event) => {
        try {
            await loadServiceType();
        } finally {
            event.detail.complete();
        }
    }

    const toggleDisabled = async () => {
        const disabled = !serviceType.isDisabled;
        await presentLoading({message: (disabled ? 'Disabling' : 'Enabling') + ' service type...'});
        try {
            setServiceType(await setServiceTypeDisabled(serviceType.serviceTypeId, disabled));
        } catch (e) {
            await presentError(e, (disabled ? 'Failed to disable' : 'Failed to enable') + ' service type');
        } finally {
            await dismissLoading();
        }
    }

    const editName = () => {
        presentAlert({
            cssClass: 'custom-alert',
            header: 'Edit service type name',
            inputs: [{name: 'name', type: 'text', placeholder: 'Enter service type name', value: serviceType.serviceTypeName}],
            buttons: [
                {text: 'Cancel', role: 'cancel'},
                {
                    text: 'Save',
                    handler: async (data) => {
                        await presentLoading({message: 'Setting service type name...'});
                        try {
                            setServiceType(await setServiceTypeName(serviceType.serviceTypeId, data.name));
                        } catch (e) {
                            await presentError(e, "Failed to set service type name");
                        } finally {
                            await dismissLoading();
                        }
                    }
                }
            ]
        })
    }

    const saveService = async () => {
        const isNew = editedService.serviceId === undefined;
        try {
            if (isNew) {
                await createService(serviceType.serviceTypeId, editedService.serviceName, editedService.serviceAdditionalInformation);
            } else {
                await updateService(editedService.serviceId, editedService.serviceName, editedService.serviceAdditionalInformation);
            }
        } catch (e) {
            await presentError(e, isNew ? "Failed to create service" : "Failed to save service");
            return;
        }
        setEditedService(null);
        await presentToast({message: isNew ? "Successfully created service" : "Successfully saved service", duration: 2000, color: "success"});
        await loadServiceType();
    }

    if (!serviceType) {
        return <IonContent fullscreen>
            <div className="custom-spinner">
                <IonSpinner></IonSpinner>
            </div>
        </IonContent>
    }

    const normalizedSearch = search.trim().toLowerCase();
    const filteredServices = normalizedSearch
        ? services.filter(service =>
            [service.serviceName, service.serviceAdditionalInformation, String(service.serviceId)]
                .some(value => value?.toLowerCase().includes(normalizedSearch)))
        : services;

    const isNewService = editedService?.serviceId === undefined;
    // the identifier in the target can't change while accesses are granted for it
    const additionalInformationLocked = !isNewService && editedService?.enabledAccessesCount > 0;

    return <IonContent fullscreen>
        <IonRefresher slot="fixed" onIonRefresh={reload}>
            <IonRefresherContent></IonRefresherContent>
        </IonRefresher>
        <IonCard>
            <IonCardHeader>
                <IonCardTitle>{ serviceType.serviceTypeName }
                    <IonButton onClick={editName} className="edit-button-icon" fill="clear" shape="round" size="small">
                        <IonIcon slot="icon-only" icon={pencil} />
                    </IonButton>
                </IonCardTitle>
                <IonCardSubtitle>{ serviceType.permissionRepositoryType } · id { serviceType.serviceTypeId }</IonCardSubtitle>
            </IonCardHeader>
            <IonButton fill="clear" color={serviceType.isDisabled ? 'success' : 'danger'} onClick={toggleDisabled}>
                {serviceType.isDisabled ? 'Enable' : 'Disable'}
            </IonButton>
        </IonCard>

        <IonItemDivider sticky className="access-list-search-bar">
            <IonButton className="ion-margin-start" fill="outline" slot="start"
                       onClick={() => setEditedService({serviceName: serviceType.serviceTypeName, serviceAdditionalInformation: ''})}>
                <IonIcon icon={add} />
            </IonButton>
            <IonSearchbar
                placeholder="Search services by name, additional information or id"
                debounce={300}
                mode="ios"
                enterkeyhint="search" inputmode="search" onIonInput={(ev) => setSearch(ev.detail.value ?? '')}></IonSearchbar>
        </IonItemDivider>

        <IonList>
            {filteredServices.length === 0 && (
                <IonItem lines="none">
                    <IonLabel color="medium">
                        {services.length === 0 ? 'No services in this service type' : 'No services match the search'}
                    </IonLabel>
                </IonItem>
            )}
            {filteredServices.map((service, index) => (
                <IonItem button key={service.serviceId}
                         lines={index === filteredServices.length - 1 ? "none" : "inset"}
                         onClick={() => setEditedService({...service, serviceAdditionalInformation: service.serviceAdditionalInformation ?? ''})}>
                    <IonLabel>
                        <h3>{ service.serviceName }</h3>
                        <p>{ service.serviceAdditionalInformation }</p>
                    </IonLabel>
                    <IonNote slot="end">
                        { service.enabledAccessesCount > 0 ? `${service.enabledAccessesCount} enabled · ` : '' }id { service.serviceId }
                    </IonNote>
                </IonItem>
            ))}
        </IonList>

        <IonModal isOpen={editedService !== null} onDidDismiss={() => setEditedService(null)}>
            <IonHeader>
                <IonToolbar>
                    <IonButtons slot="start">
                        <IonButton onClick={() => setEditedService(null)}>Cancel</IonButton>
                    </IonButtons>
                    <IonTitle>{ isNewService ? 'Create service' : 'Edit service' }</IonTitle>
                    <IonButtons slot="end">
                        <IonButton
                            disabled={!editedService?.serviceName?.trim() || !editedService?.serviceAdditionalInformation?.trim()}
                            strong={true}
                            onClick={() => saveService()}>
                            { isNewService ? 'Create' : 'Save' }
                        </IonButton>
                    </IonButtons>
                </IonToolbar>
            </IonHeader>
            <IonContent className="ion-padding">
                <IonItem>
                    <IonInput label="Service name" labelPlacement="stacked" maxlength={100}
                              placeholder="Enter service name"
                              value={editedService?.serviceName}
                              onIonInput={(ev) => setEditedService(x => ({...x, serviceName: ev.target.value}))}/>
                </IonItem>
                <IonItem>
                    <IonInput label="Additional information" labelPlacement="stacked"
                              placeholder="Identifier in the target, e.g. a role or claim"
                              disabled={additionalInformationLocked}
                              value={editedService?.serviceAdditionalInformation}
                              onIonInput={(ev) => setEditedService(x => ({...x, serviceAdditionalInformation: ev.target.value}))}/>
                </IonItem>
                {additionalInformationLocked && (
                    <IonNote className="ion-padding-top" style={{display: 'block'}}>
                        The additional information can't be changed while the service has enabled accesses. Revoke them first.
                    </IonNote>
                )}
            </IonContent>
        </IonModal>
    </IonContent>
}
